import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon} from '@primer/octicons-react'
import {ActionList, Banner, type DialogProps, Link, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {IssueField} from '../api/common-contracts'
import {checkFieldCollision} from '../helpers/field-rename-helper'
import {getInitialState} from '../helpers/initial-state'
import {useViews} from '../hooks/use-views'
import type {ColumnModel} from '../models/column-model'
import {ApiError} from '../platform/api-error'
import {useAllColumns} from '../state-providers/columns/use-all-columns'
import {useInvalidateIssueFieldQuery} from '../state-providers/issue-fields/use-issue-fields-query'
import type {RenameState} from '../state-providers/modals/add-field-modal-state-provider'
import styles from './add-issue-fields-dialog.module.css'
import {AddIssueFieldsDialogBanner} from './add-issue-fields-dialog-banner'
import {getValidationResult, ValidationResult} from './add-issue-fields-dialog-validation'
import {getColumnIcon} from './column-detail-helpers'
import {useAddField} from './table/hooks/use-add-field'

export type UseAddIssueFieldsDialogProps = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  issueFields: Array<IssueField>
  openAddColumnDialog: () => void
  onSave: (newCols: Array<ColumnModel>) => void
  issueFieldsLoading: boolean
  onSelectFieldWithCollision: (renameState: RenameState) => void
  selectedFieldIds?: Set<number>
  setSelectedFieldIds?: React.Dispatch<React.SetStateAction<Set<number>>>
  hasOrgOnlyFieldsHidden?: boolean
}

const IssueFieldsLoadingSpinner = () => (
  <div role="status" className={styles.loadingStateContainer}>
    <Spinner size="large" />
    <span className="sr-only">Loading...</span>
  </div>
)

/**
 * Custom hook that encapsulates stateful logic for the Add Issue Fields dialog.
 *
 * This hook returns dialog props that can be spread over a generic Dialog component,
 * enabling us to share the same dialog wrapper between the Add Issue Fields and Create Project Field dialogs.
 * This is needed, as Primer's Dialog component requires specifying props like Header, Body, and Footer
 * as props on the dialog itself, rather than as child components.
 *
 * @returns DialogProps object that can be spread over a Dialog component
 */
export function useAddIssueFieldsDialog({
  isOpen,
  setOpen,
  issueFields,
  openAddColumnDialog,
  onSave,
  issueFieldsLoading,
  onSelectFieldWithCollision,
  selectedFieldIds: controlledSelectedFieldIds,
  setSelectedFieldIds: setControlledSelectedFieldIds,
  hasOrgOnlyFieldsHidden,
}: UseAddIssueFieldsDialogProps): DialogProps {
  const {importIssueFields} = useAddField()
  const {currentView} = useViews()
  const {allColumns, allColumnsRef} = useAllColumns()
  const invalidateIssueFieldQuery = useInvalidateIssueFieldQuery()
  const [visibleIssueFields, setVisibleIssueFields] = useState([...issueFields])

  const [localSelectedFieldIds, setLocalSelectedFieldIds] = useState<Set<number>>(() => new Set())
  const selectedFieldIds = controlledSelectedFieldIds ?? localSelectedFieldIds
  const setSelectedFieldIds = setControlledSelectedFieldIds ?? setLocalSelectedFieldIds

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [importError, setImportError] = useState<ApiError | null>(null)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const selectedFieldIdsRef = useRef(selectedFieldIds)
  useEffect(() => {
    selectedFieldIdsRef.current = selectedFieldIds
  }, [selectedFieldIds])

  const projectFieldCount = allColumns?.length || 0
  const selectedCount = selectedFieldIds.size
  const {projectOwner} = getInitialState()
  const ownerName = projectOwner?.name?.toLowerCase()

  const validationResult = getValidationResult(selectedCount, projectFieldCount)
  const canSubmit = validationResult === ValidationResult.Valid && !isSubmitting

  useEffect(() => {
    if (!isOpen || isSubmitting || issueFieldsLoading) return

    // Otherwise, refresh the visible issue fields list
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVisibleIssueFields([...issueFields])
  }, [isOpen, isSubmitting, issueFields, issueFieldsLoading, openAddColumnDialog])

  useEffect(() => {
    if (isOpen && !controlledSelectedFieldIds) {
      setSelectedFieldIds(new Set())
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImportError(null)
      setHasAttemptedSubmit(false)
    }
  }, [controlledSelectedFieldIds, isOpen, setSelectedFieldIds])

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true)

    if (!canSubmit || !currentView) return

    setImportError(null)
    setIsSubmitting(true)

    try {
      await importIssueFields.perform({
        issueFieldIds: Array.from(selectedFieldIds),
        viewNumber: currentView.number,
      })

      if (importIssueFields.status.current.status === 'succeeded') {
        onSave(importIssueFields.status.current.data ?? [])
        setSelectedFieldIds(new Set())
        setOpen(false)
        invalidateIssueFieldQuery()
      } else if (importIssueFields.status.current.status === 'failed') {
        const hookError = importIssueFields.status.current.error
        setImportError(hookError)
        setIsSubmitting(false)
        invalidateIssueFieldQuery()
      }
    } catch {
      setImportError(new ApiError('An unexpected error occurred'))
      invalidateIssueFieldQuery()
    } finally {
      setIsSubmitting(false)
    }
  }, [
    canSubmit,
    currentView,
    importIssueFields,
    selectedFieldIds,
    onSave,
    setSelectedFieldIds,
    setOpen,
    invalidateIssueFieldQuery,
  ])

  const handleFieldToggle = useCallback(
    (field: IssueField) => {
      const isCurrentlySelected = selectedFieldIdsRef.current.has(field.id)

      if (!isCurrentlySelected) {
        const collision = checkFieldCollision(field, allColumnsRef.current)
        if (collision) {
          setSelectedFieldIds(prev => new Set(prev).add(field.id))

          onSelectFieldWithCollision({
            column: collision,
            issueFieldId: field.id,
            source: 'issue-fields-dialog',
            onRenameConfirm: async () => {},
            onRenameCancel: () => {
              setSelectedFieldIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(field.id)
                return newSet
              })
            },
          })
          return
        }
      }

      setSelectedFieldIds(prev => {
        const newSet = new Set(prev)
        if (isCurrentlySelected) {
          newSet.delete(field.id)
        } else {
          newSet.add(field.id)
        }
        return newSet
      })
    },
    [allColumnsRef, onSelectFieldWithCollision, setSelectedFieldIds],
  )

  const handleCancel = useCallback(() => {
    setSelectedFieldIds(new Set())
    setImportError(null)
    setHasAttemptedSubmit(false)
    setOpen(false)
    invalidateIssueFieldQuery()
  }, [setOpen, setSelectedFieldIds, invalidateIssueFieldQuery])

  const handleCreateProjectField = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      openAddColumnDialog()
    },
    [openAddColumnDialog],
  )

  const showBanners = !isSubmitting || importError !== null
  const hasIssueFields = visibleIssueFields.length > 0

  const footerButtons = useMemo(
    () =>
      issueFieldsLoading
        ? []
        : hasIssueFields
          ? [
              {buttonType: 'default' as const, content: 'Cancel', onClick: handleCancel},
              {
                buttonType: 'primary' as const,
                content: 'Add',
                onClick: handleSubmit,
                loading: isSubmitting,
                ...testIdProps('add-issue-fields-submit-button'),
              },
            ]
          : [{buttonType: 'default' as const, content: 'Cancel', onClick: handleCancel}],
    [issueFieldsLoading, hasIssueFields, handleCancel, handleSubmit, isSubmitting],
  )

  const subtitle = useMemo(
    () => (
      <>
        <p>Use issue fields defined by {ownerName} to keep information consistent across projects.</p>
        Need something specific to this project?{' '}
        <Link as="button" inline onClick={handleCreateProjectField} {...testIdProps('create-project-field-link')}>
          Create a project field
        </Link>
      </>
    ),
    [ownerName, handleCreateProjectField],
  )

  const renderBody = useCallback(
    () => (
      <>
        {issueFieldsLoading ? (
          <IssueFieldsLoadingSpinner />
        ) : (
          <>
            {showBanners && (
              <AddIssueFieldsDialogBanner
                projectCount={projectFieldCount}
                selectedCount={selectedCount}
                hasAttemptedSubmit={hasAttemptedSubmit}
                importError={importError}
                onRetry={handleSubmit}
              />
            )}
            {hasOrgOnlyFieldsHidden && (
              <Banner
                variant="info"
                className="m-2"
                title="Some fields are not shown"
                hideTitle
                description="Some issue fields are not available because they are set to Organization only."
                {...testIdProps('org-only-fields-hidden-notice')}
              />
            )}
            {visibleIssueFields.length > 0 ? (
              <ActionList
                className={styles.dialogBody}
                showDividers
                selectionVariant="multiple"
                role="listbox"
                {...testIdProps('add-issue-fields-list')}
              >
                {visibleIssueFields.map(field => {
                  const Icon = getColumnIcon(field.dataType)
                  const isSelected = selectedFieldIds.has(field.id)

                  return (
                    <ActionList.Item
                      key={field.id}
                      role="option"
                      aria-selected={isSelected}
                      selected={isSelected}
                      onSelect={e => {
                        e.stopPropagation()
                        handleFieldToggle(field)
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <Icon />
                      </ActionList.LeadingVisual>
                      {field.name}
                    </ActionList.Item>
                  )
                })}
              </ActionList>
            ) : (
              <Blankslate size="small" className={styles.emptyState} {...testIdProps('add-issue-fields-empty-state')}>
                <Blankslate.Visual>
                  <BookIcon size="medium" />
                </Blankslate.Visual>
                <Blankslate.Heading>No available issue fields</Blankslate.Heading>
                <Blankslate.Description>
                  Ask your organization admin to add issue fields, or{' '}
                  <Link as="button" inline onClick={handleCreateProjectField}>
                    create a local project field
                  </Link>
                  .
                </Blankslate.Description>
              </Blankslate>
            )}
          </>
        )}
      </>
    ),
    [
      issueFieldsLoading,
      showBanners,
      projectFieldCount,
      selectedCount,
      hasAttemptedSubmit,
      handleSubmit,
      visibleIssueFields,
      selectedFieldIds,
      handleFieldToggle,
      handleCreateProjectField,
      importError,
      hasOrgOnlyFieldsHidden,
    ],
  )

  return {
    className: styles.dialog,
    title: 'Add issue fields',
    subtitle,
    onClose: handleCancel,
    ...testIdProps('add-issue-fields-dialog'),
    footerButtons,
    renderBody,
  }
}
